import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

let globalOtp = ''; 

// Yup validation schema
const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    mobileNo: Yup.string()
        .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit mobile number')
        .required('Mobile number is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    experience: Yup.string().required('Experience is required'),
    location: Yup.string().required('Location is required'),
    details: Yup.string().required('Details are required'),
    city: Yup.string().required('City is required'),
    pincode: Yup.string().matches(/^[0-9]{6}$/, 'Pincode should be 6 digits').required('Pincode is required'),
    address: Yup.string().required('Address is required'),
    callMailAccepted: Yup.bool().oneOf([true], 'You must accept the terms'),
});

const errorMessageStyle = {
    color: 'red',
    fontSize: '0.9em',
    marginTop: '4px',
};

const FranchiseeEnquiryForm = () => {
    const [otpSent, setOtpSent] = useState(false);
    const [otpInput, setOtpInput] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpVerified, setOtpVerified] = useState(false);
    const [thankYouMessage, setThankYouMessage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState(60); // Timer for OTP expiration
    const [timerActive, setTimerActive] = useState(false); // To control the timer
    const [otpResendEnabled, setOtpResendEnabled] = useState(false);
    const [mobileDisabled, setMobileDisabled] = useState(false);

    // Handle OTP sending
    const handleSendOtp = async (mobileNo) => {
        // Prevent sending OTP if mobile number is empty
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/UserDetails/CheckValidNumberOrNot?mobileNo=${mobileNo}`);
            if (response.data.success) {
                setOtpSent(true);
                setOtpError('');
                setOtpInput(''); // Clear any previous OTP input
                setOtpVerified(false); // Reset verification status
                setTimer(60); // Reset the timer
                setTimerActive(true); // Start the timer
                setOtpResendEnabled(false);
                 setMobileDisabled(true);
            } else {
                setOtpError('Failed to send OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            setOtpError('An error occurred while sending OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle OTP verification
    const handleOtpVerify = async (mobileNo) => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/UserDetails/VerifyOtp?mobileNo=${mobileNo}&otp=${otpInput}`);
            if (response.data.success) {
                setOtpVerified(true);
                setOtpError('');
                toast.success('OTP verified successfully!');
                setTimerActive(false); 
            } else {
                setOtpError(response.data.message || 'Invalid OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setOtpError('An error occurred while verifying OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Set timeout for OTP expiration
    useEffect(() => {
        if (timerActive && timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);

            return () => clearInterval(interval);
        } else if (timer === 0) {
            setOtpSent(false); // Reset OTP sent state
            setOtpError('OTP expired. Please request a new OTP.');
            setOtpVerified(false); // Reset verified status
            setTimerActive(false); // Stop the timer
            setOtpResendEnabled(true); // Enable resend button
            setMobileDisabled(false); 
        }
    }, [timerActive, timer]);

    // Handle form submission
    const handleSubmit = async (values, { resetForm }) => {
        if (!otpVerified) {
            toast.error('Please verify the OTP before submitting the form.');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Enquiry/InsertFranchiseEnquiry`, {
                name: values.name,
                mobileNo: values.mobileNo,
                email: values.email,
                experience: values.experience,
                location: values.location,
                details: values.details,
                city: values.city,
                pincode: values.pincode,
                address: values.address,
            });
            if (response.data.success) {
                setThankYouMessage(true); // Show thank-you message
                setOtpSent(false);
                setOtpVerified(false);
                setOtpInput('');
                resetForm();

                // Auto-refresh after 5 seconds
                setTimeout(() => {
                    setThankYouMessage(false);
                    window.location.reload(); // Refresh the page
                }, 6000); // Refresh after 6 seconds
            } else {
                toast.error('Failed to submit enquiry. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting enquiry:', error);
            toast.error('An error occurred while submitting enquiry. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Formik
                initialValues={{
                    name: '',
                    mobileNo: '',
                    email: '',
                    experience: '',
                    location: '',
                    details: '',
                    city: '',
                    pincode: '',
                    address: '',
                    callMailAccepted: false,
                }}
                validationSchema={validationSchema}
                validateOnBlur={true} // Enable validation on blur
                validateOnChange={true} // Enable validation on change
                onSubmit={handleSubmit}
            >
                {({ values }) => (
                    <Form>
                        <div className="uk-child-width-1-2@m uk-grid-small uk-text-left uk-grid-match" data-uk-grid>
                            <div className="uk-width-1-1">
                                <div>
                                    <label className="uk-form-label uk-text-bold">Name:</label><br />
                                    <Field type="text" name="name" className="uk-input" />
                                    <ErrorMessage name="name" component="div" style={{ color: 'red' }} />
                                </div>
                            </div>

                            <div>
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Mobile No:</label>
                                    <Field
                                        type="text"
                                        name="mobileNo"
                                        className='uk-input'
                                        value={values.mobileNo} // Bind value from Formik
                                        disabled={mobileDisabled} // Disable input if OTP is verified// Disable mobile number field after OTP verification
                                    />
                                    <ErrorMessage style={errorMessageStyle} name="mobileNo" component="div" className="error-message" />
                                    {!otpVerified && (
                                        <button
                                            type="button" className='uk-margin-small-top'
                                            onClick={() => handleSendOtp(values.mobileNo)}
                                            disabled={!values.mobileNo || otpSent || loading} // Disable if mobile number is empty or loading
                                        >
                                            {loading ? 'Sending...' : 'Send OTP'}
                                        </button>
                                    )}
                                </div>
                            </div>
                             <div>
                            <div>
                                <div className="uk-text-left">
                                    {otpSent && (
                                        <div>
                                            <label className="uk-form-label uk-text-bold">Enter OTP:</label>
                                            <Field
                                                type="text"
                                                name="otpsend"
                                                className="uk-input"
                                                value={otpInput}
                                                onChange={(e) => setOtpInput(e.target.value)}
                                                disabled={otpVerified} // Disable input if OTP is verified
                                                required
                                            />
                                            {!otpVerified && (
                                                <button
                                                    type="button"
                                                    className="uk-margin-small"
                                                    onClick={() => handleOtpVerify(values.mobileNo)}
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Verifying...' : 'Verify OTP'}
                                                </button>
                                            )}
                                            {otpError && <span style={{ color: 'red' }}>{otpError}</span>}
                                            <div>
                                                {otpVerified ? (
                                                    <span style={{ color: 'green' }}>OTP is verified</span>
                                                ) : (
                                                    <span style={{ color: 'red' }}>{timer > 0 ? `${timer} seconds remaining` : 'OTP expired'}</span>
                                                )}
                                            </div>
                                            {otpResendEnabled && !otpVerified && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSendOtp(values.mobileNo)}
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Sending...' : 'Resend OTP'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                            <div>
                                <div>
                                    <label className="uk-form-label uk-text-bold">Email:</label><br />
                                    <Field type="email" name="email" className="uk-input" />
                                    <ErrorMessage name="email" component="div" style={{ color: 'red' }} />
                                </div>
                            </div>

                            <div>
                                <div>
                                    <label className="uk-form-label uk-text-bold">Experience:</label><br />
                                    <Field type="text" className="uk-input" name="experience" />
                                    <ErrorMessage name="experience" component="div" style={{ color: 'red' }} />
                                </div>
                            </div>

                            <div>
                                <div>
                                    <label className="uk-form-label uk-text-bold">Location:</label><br />
                                    <Field type="text" className="uk-input" name="location" />
                                    <ErrorMessage name="location" component="div" style={{ color: 'red' }} />
                                </div>
                            </div>

                            <div>
                                <div>
                                    <label className="uk-form-label uk-text-bold">Details:</label><br />
                                    <Field type="text" className="uk-input" name="details" />
                                    <ErrorMessage name="details" component="div" style={{ color: 'red' }} />
                                </div>
                            </div>

                            <div>
                                <div>
                                    <label className="uk-form-label uk-text-bold">City:</label><br />
                                    <Field type="text" className="uk-input" name="city" />
                                    <ErrorMessage name="city" component="div" style={{ color: 'red' }} />
                                </div>
                            </div>

                            <div>
                                <div>
                                    <label className="uk-form-label uk-text-bold">Pincode:</label><br />
                                    <Field type="text" className="uk-input" name="pincode" />
                                    <ErrorMessage name="pincode" component="div" style={{ color: 'red' }} />
                                </div>
                            </div>

                            <div>
                                <div>
                                    <label className="uk-form-label uk-text-bold">Address:</label><br />
                                    <Field type="text" className="uk-input" name="address" />
                                    <ErrorMessage name="address" component="div" style={{ color: 'red' }} />
                                </div>
                            </div>

                            <div className='uk-width-1-1'>
                                <div className="uk-text-left">
                                    <Field type="checkbox" name="callMailAccepted" className="uk-checkbox" />
                                    <label className="uk-form-label uk-text-bold uk-margin-small-left">
                                    I agree and allow STRATOS AIRMAIL representative to call or e-mail regarding the enquiry.
                                    </label>
                                    <ErrorMessage style={errorMessageStyle} name="callMailAccepted" component="div" className="error-message" />
                                </div>
                            </div>

                            <div className="uk-margin">
                                <button
                                    type="submit"
                                    className="button uk-button-primary uk-margin-top"
                                    disabled={!otpVerified || loading}
                                >
                                    {loading ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>

            {thankYouMessage && (
                <div className="uk-alert-success">
                    <p>Thank you for submitting your enquiry! We will get back to you soon.</p>
                </div>
            )}

            <ToastContainer />
        </div>
    );
};

export default FranchiseeEnquiryForm;
